// src/pages/clients/Installations.js
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Grid,
  MenuItem,
  Chip,
  IconButton,
  Tooltip,
  Alert,
  CircularProgress,
  Autocomplete,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Menu,
  Checkbox,
  InputAdornment,
  TableSortLabel,
  FormControl,
  Select,
  Radio,
  RadioGroup,
  FormControlLabel,
  Divider
} from '@mui/material';
import {
  Add as AddIcon,
  CheckCircle as ActivateIcon,
  Receipt as InvoiceIcon,
  Cancel as CancelIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Refresh as RefreshIcon,
  ViewColumn as ViewColumnIcon,
  Search as SearchIcon
} from '@mui/icons-material';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { es } from 'date-fns/locale';
import CoordinateSelector from '../../components/maps/CoordinateSelector';

// Función auxiliar para validar y formatear coordenadas
const formatCoordinatesForAPI = (coordinates) => {
  if (!coordinates || coordinates.trim() === '') {
    return '';
  }

  try {
    // Limpiar cualquier formato extraño
    let cleanCoords = coordinates.replace(/[()XY']/g, '').trim();

    if (cleanCoords.includes(',')) {
      const parts = cleanCoords.split(',').map(c => c.trim());
      if (parts.length === 2) {
        const num1 = parseFloat(parts[0]);
        const num2 = parseFloat(parts[1]);

        if (!isNaN(num1) && !isNaN(num2)) {
          // Verificar rangos válidos para determinar el orden
          if (Math.abs(num1) <= 90 && Math.abs(num2) <= 180) {
            // num1 es probablemente latitud, num2 longitud
            return `${num1}, ${num2}`;
          } else if (Math.abs(num2) <= 90 && Math.abs(num1) <= 180) {
            // num2 es probablemente latitud, num1 longitud
            return `${num2}, ${num1}`;
          } else {
            // Mantener orden original si no podemos determinar
            return `${num1}, ${num2}`;
          }
        }
      }
    }
  } catch (error) {
    console.warn('Error al formatear coordenadas:', error);
  }

  return coordinates; // Retornar original si no se puede procesar
};

// Función para procesar coordenadas desde el backend
const processCoordinatesFromAPI = (coordinates) => {
  if (!coordinates) {
    return '';
  }

  try {
    if (typeof coordinates === 'object' && coordinates !== null) {
      // Si es un objeto point de PostgreSQL (x,y) donde x=lng, y=lat
      if (coordinates.x !== undefined && coordinates.y !== undefined) {
        return `${coordinates.y}, ${coordinates.x}`;
      }
    } else if (typeof coordinates === 'string') {
      // Si ya es string, formatear correctamente
      return formatCoordinatesForAPI(coordinates);
    }
  } catch (e) {
    console.error('Error al procesar coordenadas:', e);
  }

  return '';
};

const EMPTY_ARRAY = [];

const InstallationForm = ({ open, onClose, onSubmit, zones = EMPTY_ARRAY, plans = EMPTY_ARRAY, routers = EMPTY_ARRAY, technicians = EMPTY_ARRAY, nodes = EMPTY_ARRAY, accessPoints = EMPTY_ARRAY, installation = null }) => {
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    dni: '',
    email: '',
    phone: '',
    address: '',
    department: '',
    province: '',
    district: '',
    installationDate: null,
    installationCost: '',
    comments: '',
    queueName: '',
    ipAddress: '',
    macAddress: '',
    routerId: '',
    planId: '',
    zoneId: '',
    nodeId: '',
    accessPointId: '',
    technicianId: '',
    coordinates: '',
    installationStatus: 'pending',
    antenna_acquisition_type: 'rental',
    antenna_price: '0.00',
    router_acquisition_type: 'rental',
    router_price: '0.00'
  });

  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [availableIps, setAvailableIps] = useState([]);
  const [loadingIps, setLoadingIps] = useState(false);

  // Actualizar el formulario solo cuando se abre la modal o cambia la instalación a editar
  useEffect(() => {
    if (!open) return;
    if (installation) {
      const apId = installation.accessPointId || installation.access_point_id || '';
      const apObj = (accessPoints || []).find(ap => ap.id === apId);
      setFormData({
        id: installation.id,
        firstName: installation.firstName || installation.first_name || '',
        lastName: installation.lastName || installation.last_name || '',
        dni: installation.dni || '',
        email: installation.email || '',
        phone: installation.phone || '',
        address: installation.address || '',
        department: installation.department || '',
        province: installation.province || '',
        district: installation.district || '',
        installationDate: installation.installationDate ? new Date(installation.installationDate) : null,
        installationCost: installation.installationCost || installation.installation_cost || '',
        comments: installation.comments || '',
        queueName: installation.queueName || installation.queue_name || '',
        ipAddress: installation.ipAddress || installation.ip_address || '',
        macAddress: installation.macAddress || installation.mac_address || '',
        antenna_acquisition_type: installation.antenna_acquisition_type || 'rental',
        antenna_price: installation.antenna_price || '0.00',
        router_acquisition_type: installation.router_acquisition_type || 'rental',
        router_price: installation.router_price || '0.00',
        routerId: installation.routerId || installation.router_id || '',
        planId: installation.planId || installation.plan_id || '',
        zoneId: installation.zoneId || installation.zone_id || '',
        nodeId: apObj ? apObj.node_id : (installation.nodeId || ''),
        accessPointId: apId,
        technicianId: installation.technicianId || installation.technician_id || '',
        coordinates: processCoordinatesFromAPI(installation.coordinates),
        installationStatus: installation.installationStatus || installation.status || 'pending'
      });
    } else {
      // Reiniciar el formulario cuando se crea una nueva instalación
      setFormData({
        firstName: '',
        lastName: '',
        dni: '',
        email: '',
        phone: '',
        address: '',
        department: '',
        province: '',
        district: '',
        installationDate: null,
        installationCost: '',
        comments: '',
        queueName: '',
        ipAddress: '',
        macAddress: '',
        routerId: '',
        planId: '',
        zoneId: '',
        nodeId: '',
        accessPointId: '',
        technicianId: '',
        coordinates: '',
        installationStatus: 'pending',
        antenna_acquisition_type: 'rental',
        antenna_price: '0.00',
        router_acquisition_type: 'rental',
        router_price: '0.00'
      });
    }
    setError('');
  }, [installation, open]);

  // Fetch available IPs when router or AP changes
  useEffect(() => {
    const fetchAvailableIps = async () => {
      if (!formData.routerId) {
        setAvailableIps([]);
        return;
      }
      setLoadingIps(true);
      try {
        let url = `/api/routers/${formData.routerId}/available-ips`;
        if (formData.accessPointId) {
          url += `?access_point_id=${formData.accessPointId}`;
        }
        const response = await fetch(url, {
          headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
        });
        if (response.ok) {
          const data = await response.json();
          setAvailableIps(data);
        }
      } catch (err) {
        console.error('Error fetching available IPs:', err);
      } finally {
        setLoadingIps(false);
      }
    };
    fetchAvailableIps();
  }, [formData.routerId, formData.accessPointId]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    setError(''); // Limpiar error al cambiar valores
  };

  const handleZoneChange = (e) => {
    const value = e.target.value;
    setFormData(prev => ({
      ...prev,
      zoneId: value,
      routerId: '',
      nodeId: '',
      accessPointId: '',
      ipAddress: ''
    }));
    setError('');
  };

  const handleRouterChange = (e) => {
    const value = e.target.value;
    setFormData(prev => ({
      ...prev,
      routerId: value,
      accessPointId: '',
      ipAddress: ''
    }));
    setError('');
  };

  const handleNodeChange = (e) => {
    const value = e.target.value;
    setFormData(prev => ({
      ...prev,
      nodeId: value,
      accessPointId: '',
      ipAddress: ''
    }));
    setError('');
  };

  const handleAPChange = (e) => {
    const value = e.target.value;
    setFormData(prev => ({
      ...prev,
      accessPointId: value,
      ipAddress: ''
    }));
    setError('');
  };

  const handleFormSubmit = (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    // Validaciones completas
    if (!formData.firstName?.trim() || !formData.lastName?.trim()) {
      setError('Nombre y apellido son obligatorios');
      setLoading(false);
      return;
    }

    if (!formData.queueName?.trim()) {
      setError('El nombre de la Queue es obligatorio');
      setLoading(false);
      return;
    }

    if (!formData.ipAddress?.trim()) {
      setError('La dirección IP es obligatoria');
      setLoading(false);
      return;
    }

    // Validar formato de IP
    const ipRegex = /^(?:(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.){3}(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)$/;
    if (!ipRegex.test(formData.ipAddress)) {
      setError('El formato de la dirección IP no es válido');
      setLoading(false);
      return;
    }

    if (!formData.routerId) {
      setError('Debe seleccionar un router');
      setLoading(false);
      return;
    }

    if (!formData.planId) {
      setError('Debe seleccionar un plan');
      setLoading(false);
      return;
    }

    if (!formData.zoneId) {
      setError('Debe seleccionar una zona');
      setLoading(false);
      return;
    }

    if (!formData.technicianId) {
      setError('Debe seleccionar un técnico');
      setLoading(false);
      return;
    }

    if (!formData.installationDate) {
      setError('La fecha de instalación es obligatoria');
      setLoading(false);
      return;
    }

    const cost = parseFloat(formData.installationCost);
    if (isNaN(cost) || cost < 0) {
      setError('El costo de instalación debe ser un número válido mayor o igual a 0');
      setLoading(false);
      return;
    }

    // Crear una copia del objeto con la fecha correctamente formateada
    const formattedData = { ...formData };

    // Formatear la fecha para enviar al backend
    if (formData.installationDate) {
      try {
        const date = new Date(formData.installationDate);
        if (!isNaN(date.getTime())) {
          // Formato ISO para backend (YYYY-MM-DD)
          formattedData.installationDate = date.toISOString().split('T')[0];
        } else {
          setError('Formato de fecha de instalación inválido');
          setLoading(false);
          return;
        }
      } catch (err) {
        setError('Error al procesar la fecha: ' + err.message);
        setLoading(false);
        return;
      }
    }

    // Formatear coordenadas
    formattedData.coordinates = formatCoordinatesForAPI(formData.coordinates);

    // Mapear campos al backend
    formattedData.router_id = formData.routerId || null;
    formattedData.plan_id = formData.planId || null;
    formattedData.zone_id = formData.zoneId || null;
    formattedData.access_point_id = formData.accessPointId || null;

    // Si todas las validaciones pasan, enviar datos formateados
    onSubmit(formattedData)
      .catch(error => {
        console.error('Error guardando instalación:', error);
        setError(error.message || 'Error al guardar instalación');
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const filteredRouters = routers.filter(r => !formData.zoneId || String(r.zone_id) === String(formData.zoneId));
  const filteredNodes = nodes.filter(n => !formData.zoneId || !n.zone_id || String(n.zone_id) === String(formData.zoneId));
  const filteredAPs = accessPoints.filter(ap => 
    (!formData.routerId || !ap.router_id || String(ap.router_id) === String(formData.routerId)) &&
    (!formData.nodeId || !ap.node_id || String(ap.node_id) === String(formData.nodeId))
  );

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <form onSubmit={handleFormSubmit}>
        <DialogTitle>
          {installation ? 'Editar Instalación' : 'Nueva Instalación'}
        </DialogTitle>
        <DialogContent>
          <Box className="p-4">
            {error && (
              <Alert severity="error" className="mb-4">
                {error}
              </Alert>
            )}

            <Typography variant="h6" className="mb-4">Datos del Cliente</Typography>
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Nombres *"
                  name="firstName"
                  value={formData.firstName}
                  onChange={handleChange}
                  required
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Apellidos *"
                  name="lastName"
                  value={formData.lastName}
                  onChange={handleChange}
                  required
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="DNI"
                  name="dni"
                  value={formData.dni}
                  onChange={handleChange}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Email"
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleChange}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Teléfono *"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  required
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Dirección *"
                  name="address"
                  value={formData.address}
                  onChange={handleChange}
                  required
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label="Departamento"
                  name="department"
                  value={formData.department}
                  onChange={handleChange}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label="Provincia"
                  name="province"
                  value={formData.province}
                  onChange={handleChange}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label="Distrito"
                  name="district"
                  value={formData.district}
                  onChange={handleChange}
                />
              </Grid>
              <Grid item xs={12}>
                <CoordinateSelector
                  value={formData.coordinates}
                  onChange={(newCoordinates) => handleChange({
                    target: { name: 'coordinates', value: newCoordinates }
                  })}
                  label="Coordenadas"
                  helperText="Selecciona la ubicación exacta de la instalación"
                />
              </Grid>
            </Grid>

            <Typography variant="h6" className="mt-6 mb-4">Datos de Conexión</Typography>
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  select
                  label="Zona *"
                  name="zoneId"
                  value={formData.zoneId}
                  onChange={handleZoneChange}
                  required
                >
                  <MenuItem value="">
                    <em>Seleccione una zona</em>
                  </MenuItem>
                  {zones.map(zone => (
                    <MenuItem key={zone.id} value={zone.id}>
                      {zone.name}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  select
                  label="Router *"
                  name="routerId"
                  value={formData.routerId}
                  onChange={handleRouterChange}
                  required
                  disabled={!formData.zoneId}
                  helperText={!formData.zoneId ? "Seleccione una Zona primero" : ""}
                >
                  <MenuItem value="">
                    <em>Seleccione un router</em>
                  </MenuItem>
                  {filteredRouters.map(router => (
                    <MenuItem key={router.id} value={router.id}>
                      {router.name} ({router.ip})
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  select
                  label="Torre / Nodo (opcional - asignado por técnico)"
                  name="nodeId"
                  value={formData.nodeId}
                  onChange={handleNodeChange}
                  disabled={!formData.zoneId}
                  helperText={!formData.zoneId ? "Seleccione una Zona primero" : "El técnico en campo asignará la torre/AP al instalar"}
                >
                  <MenuItem value="">
                    <em>Ninguna / Sin Torre</em>
                  </MenuItem>
                  {filteredNodes.map(node => (
                    <MenuItem key={node.id} value={node.id}>
                      {node.name}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  select
                  label="AP / Sectorial (opcional - asignado por técnico)"
                  name="accessPointId"
                  value={formData.accessPointId}
                  onChange={handleAPChange}
                  disabled={!formData.zoneId}
                  helperText={!formData.zoneId ? "Seleccione una Zona primero" : "El técnico en campo asignará el AP al instalar"}
                >
                  <MenuItem value="">
                    <em>Ninguno / Sin AP</em>
                  </MenuItem>
                  {filteredAPs.map(ap => (
                    <MenuItem key={ap.id} value={ap.id}>
                      {ap.name} ({ap.ssid})
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  select
                  label="Plan *"
                  name="planId"
                  value={formData.planId}
                  onChange={handleChange}
                  required
                >
                  <MenuItem value="">
                    <em>Seleccione un plan</em>
                  </MenuItem>
                  {plans.map(plan => (
                    <MenuItem key={plan.id} value={plan.id}>
                      {plan.name} - ${plan.price}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Nombre Queue *"
                  name="queueName"
                  value={formData.queueName}
                  onChange={handleChange}
                  required
                  helperText="Nombre único para identificar la conexión"
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <Autocomplete
                  freeSolo
                  options={availableIps}
                  loading={loadingIps}
                  value={formData.ipAddress}
                  onChange={(event, newValue) => {
                    setFormData(prev => ({ ...prev, ipAddress: newValue || '' }));
                  }}
                  onInputChange={(event, newInputValue) => {
                    setFormData(prev => ({ ...prev, ipAddress: newInputValue || '' }));
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Dirección IP *"
                      required
                      helperText={
                        loadingIps
                          ? "Buscando IPs libres..."
                          : formData.routerId
                          ? `${availableIps.length} IPs libres encontradas`
                          : "Seleccione un Router para ver IPs disponibles"
                      }
                      InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                          <>
                            {loadingIps ? <CircularProgress color="inherit" size={20} /> : null}
                            {params.InputProps.endAdornment}
                          </>
                        ),
                      }}
                    />
                  )}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Dirección MAC"
                  name="macAddress"
                  value={formData.macAddress}
                  onChange={handleChange}
                  helperText="Ejemplo: AA:BB:CC:DD:EE:FF"
                />
              </Grid>
            </Grid>

            <Typography variant="h6" className="mt-6 mb-4">Datos de Instalación</Typography>
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Costo de Instalación *"
                  name="installationCost"
                  type="number"
                  value={formData.installationCost}
                  onChange={handleChange}
                  required
                  inputProps={{ min: 0, step: 0.01 }}
                  helperText="Ingrese el costo en soles"
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={es}>
                  <DatePicker
                    label="Fecha de Instalación *"
                    value={formData.installationDate}
                    onChange={(date) => handleChange({
                      target: { name: 'installationDate', value: date }
                    })}
                    slotProps={{
                      textField: {
                        fullWidth: true,
                        required: true,
                        error: !formData.installationDate,
                        helperText: !formData.installationDate ? 'Campo requerido' : ''
                      }
                    }}
                  />
                </LocalizationProvider>
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  select
                  label="Técnico *"
                  name="technicianId"
                  value={formData.technicianId}
                  onChange={handleChange}
                  required
                >
                  <MenuItem value="">
                    <em>Seleccione un técnico</em>
                  </MenuItem>
                  {technicians.filter(tech => tech.status === 'active').map(tech => (
                    <MenuItem key={tech.id} value={tech.id}>
                      {tech.name}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  multiline
                  rows={3}
                  label="Comentarios"
                  name="comments"
                  value={formData.comments}
                  onChange={handleChange}
                  helperText="Observaciones adicionales sobre la instalación"
                />
              </Grid>
            </Grid>

            <Typography variant="h6" className="mt-6 mb-4">Términos Comerciales de Equipos</Typography>
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  select
                  label="Modo de Adquisición - Antena/CPE"
                  name="antenna_acquisition_type"
                  value={formData.antenna_acquisition_type}
                  onChange={handleChange}
                >
                  <MenuItem value="rental">Alquiler / Garantía</MenuItem>
                  <MenuItem value="sale">Venta</MenuItem>
                  <MenuItem value="none">Ninguno</MenuItem>
                </TextField>
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Precio Sugerido Antena/CPE (S/)"
                  name="antenna_price"
                  type="number"
                  value={formData.antenna_price}
                  onChange={handleChange}
                  inputProps={{ min: 0, step: 0.01 }}
                  disabled={formData.antenna_acquisition_type === 'none'}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  select
                  label="Modo de Adquisición - Router WiFi"
                  name="router_acquisition_type"
                  value={formData.router_acquisition_type}
                  onChange={handleChange}
                >
                  <MenuItem value="rental">Alquiler / Garantía</MenuItem>
                  <MenuItem value="sale">Venta</MenuItem>
                  <MenuItem value="none">Ninguno</MenuItem>
                </TextField>
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Precio Sugerido Router WiFi (S/)"
                  name="router_price"
                  type="number"
                  value={formData.router_price}
                  onChange={handleChange}
                  inputProps={{ min: 0, step: 0.01 }}
                  disabled={formData.router_acquisition_type === 'none'}
                />
              </Grid>
            </Grid>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose} disabled={loading}>
            Cancelar
          </Button>
          <Button
            type="submit"
            variant="contained"
            color="primary"
            disabled={loading}
            startIcon={loading ? <CircularProgress size={20} /> : null}
          >
            {loading ? 'Guardando...' : (installation ? 'Actualizar' : 'Crear')}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

// Definición de todas las columnas disponibles para la configuración de visibilidad
const ALL_COLUMNS = [
  { id: 'client_name', label: 'Cliente', alwaysVisible: true },
  { id: 'queue_name', label: 'Queue' },
  { id: 'ip_address', label: 'IP' },
  { id: 'address', label: 'Dirección' },
  { id: 'technician', label: 'Técnico' },
  { id: 'installation_date', label: 'Fecha Instalación' },
  { id: 'installation_cost', label: 'Costo' },
  { id: 'status', label: 'Estado' }
];

const DEFAULT_VISIBLE_COLUMNS = ALL_COLUMNS.map(c => c.id);
const COLUMN_STORAGE_KEY = 'yinowisp_installations_columns';

const loadSavedColumns = () => {
  try {
    const saved = localStorage.getItem(COLUMN_STORAGE_KEY);
    if (saved) {
      const parsed = JSON.parse(saved);
      if (Array.isArray(parsed) && parsed.length > 0) return parsed;
    }
  } catch (e) { /* ignore */ }
  return DEFAULT_VISIBLE_COLUMNS;
};

const Installations = () => {
  const [installations, setInstallations] = useState([]);
  const [filteredInstallations, setFilteredInstallations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openForm, setOpenForm] = useState(false);
  const [selectedInstallation, setSelectedInstallation] = useState(null);
  const [zones, setZones] = useState([]);
  const [plans, setPlans] = useState([]);
  const [routers, setRouters] = useState([]);
  const [technicians, setTechnicians] = useState([]);
  const [nodes, setNodes] = useState([]);
  const [accessPoints, setAccessPoints] = useState([]);
  const [error, setError] = useState(null);
  const [refreshing, setRefreshing] = useState(false);

  // Estados para el modal de facturación de instalación (pago único / crédito)
  const [creditModalOpen, setCreditModalOpen] = useState(false);
  const [creditModalLoading, setCreditModalLoading] = useState(false);
  const [targetInstallation, setTargetInstallation] = useState(null);
  const [paymentType, setPaymentType] = useState('cash'); // 'cash' o 'credit'
  const [installmentsCount, setInstallmentsCount] = useState(3);
  const [installments, setInstallments] = useState([]); // [{ amount, payment_due_date, due_date }]
  const [installationTotal, setInstallationTotal] = useState(0);
  const [actionInProgress, setActionInProgress] = useState(false);

  // Estados para búsqueda, paginación, responsive y visibilidad de columnas
  const [searchTerm, setSearchTerm] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [visibleColumns, setVisibleColumns] = useState(loadSavedColumns);
  const [columnSettingsAnchor, setColumnSettingsAnchor] = useState(null);
  const [sortConfig, setSortConfig] = useState({ key: 'client_name', direction: 'asc' });

  const requestSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  const sortedInstallations = useMemo(() => {
    let sortableItems = [...filteredInstallations];
    if (sortConfig.key !== null) {
      sortableItems.sort((a, b) => {
        let aVal = a[sortConfig.key];
        let bVal = b[sortConfig.key];

        // Manejo especial para valores nulos/vacíos
        if (aVal === null || aVal === undefined) aVal = '';
        if (bVal === null || bVal === undefined) bVal = '';

        if (sortConfig.key === 'installation_cost') {
          const aNum = parseFloat(aVal || 0);
          const bNum = parseFloat(bVal || 0);
          return sortConfig.direction === 'asc' ? aNum - bNum : bNum - aNum;
        }

        // Caso especial para IP Address (numérico por octetos)
        if (sortConfig.key === 'ip_address') {
          const ipToParts = (ip) => {
            if (!ip) return [0, 0, 0, 0];
            const cleanIp = ip.split('/')[0].trim();
            const parts = cleanIp.split('.').map(p => parseInt(p, 10) || 0);
            while (parts.length < 4) parts.push(0);
            return parts;
          };

          const aParts = ipToParts(aVal);
          const bParts = ipToParts(bVal);

          for (let i = 0; i < 4; i++) {
            if (aParts[i] !== bParts[i]) {
              return sortConfig.direction === 'asc' ? aParts[i] - bParts[i] : bParts[i] - aParts[i];
            }
          }
          return 0;
        }

        // Conversión a string para ordenamiento seguro (case insensitive)
        const aStr = String(aVal).toLowerCase();
        const bStr = String(bVal).toLowerCase();

        if (aStr < bStr) {
          return sortConfig.direction === 'asc' ? -1 : 1;
        }
        if (aStr > bStr) {
          return sortConfig.direction === 'asc' ? 1 : -1;
        }
        return 0;
      });
    }
    return sortableItems;
  }, [filteredInstallations, sortConfig]);

  const isColumnVisible = (colId) => visibleColumns.includes(colId);

  const handleColumnToggle = (colId) => {
    const col = ALL_COLUMNS.find(c => c.id === colId);
    if (col?.alwaysVisible) return;
    setVisibleColumns(prev => {
      const next = prev.includes(colId)
        ? prev.filter(id => id !== colId)
        : [...prev, colId];
      localStorage.setItem(COLUMN_STORAGE_KEY, JSON.stringify(next));
      return next;
    });
  };

  const handleResetColumns = () => {
    setVisibleColumns(DEFAULT_VISIBLE_COLUMNS);
    localStorage.setItem(COLUMN_STORAGE_KEY, JSON.stringify(DEFAULT_VISIBLE_COLUMNS));
  };

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    fetchInitialData();
  }, []);

  useEffect(() => {
    if (searchTerm.trim() === '') {
      setFilteredInstallations(installations);
    } else {
      const lowercasedFilter = searchTerm.toLowerCase();
      const filtered = installations.filter(inst => {
        return (
          (inst.client_name && inst.client_name.toLowerCase().includes(lowercasedFilter)) ||
          (inst.queue_name && inst.queue_name.toLowerCase().includes(lowercasedFilter)) ||
          (inst.ip_address && inst.ip_address.toLowerCase().includes(lowercasedFilter)) ||
          (inst.address && inst.address.toLowerCase().includes(lowercasedFilter)) ||
          (inst.technician && inst.technician.toLowerCase().includes(lowercasedFilter)) ||
          (inst.status && inst.status.toLowerCase().includes(lowercasedFilter))
        );
      });
      setFilteredInstallations(filtered);
    }
    setPage(0);
  }, [searchTerm, installations]);

  const fetchInitialData = async () => {
    try {
      setLoading(true);
      setError(null);

      const token = localStorage.getItem('token');
      if (!token) {
        throw new Error('No se encontró token de autenticación');
      }

      const headers = {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      };

      const [installationsRes, zonesRes, plansRes, routersRes, techniciansRes, nodesRes, apsRes] = await Promise.all([
        fetch('/api/installations', { headers }),
        fetch('/api/zones', { headers }),
        fetch('/api/plans', { headers }),
        fetch('/api/routers', { headers }),
        fetch('/api/technicians', { headers }),
        fetch('/api/nodes', { headers }).catch(() => ({ ok: false })),
        fetch('/api/access-points', { headers }).catch(() => ({ ok: false }))
      ]);

      // Verificar respuestas
      if (!installationsRes.ok) {
        throw new Error(`Error al cargar instalaciones: ${installationsRes.status}`);
      }
      if (!zonesRes.ok) {
        throw new Error(`Error al cargar zonas: ${zonesRes.status}`);
      }
      if (!plansRes.ok) {
        throw new Error(`Error al cargar planes: ${plansRes.status}`);
      }
      if (!routersRes.ok) {
        throw new Error(`Error al cargar routers: ${routersRes.status}`);
      }
      if (!techniciansRes.ok) {
        throw new Error(`Error al cargar técnicos: ${techniciansRes.status}`);
      }

      const [installationsData, zonesData, plansData, routersData, techniciansData, nodesData, apsData] = await Promise.all([
        installationsRes.json(),
        zonesRes.json(),
        plansRes.json(),
        routersRes.json(),
        techniciansRes.json(),
        nodesRes.ok ? nodesRes.json() : [],
        apsRes.ok ? apsRes.json() : []
      ]);

      // Procesar las coordenadas de las instalaciones al formato correcto para la interfaz
      const processedInstallations = installationsData.map(installation => ({
        ...installation,
        coordinates: processCoordinatesFromAPI(installation.coordinates)
      }));

      setInstallations(processedInstallations);
      setZones(zonesData);
      setPlans(plansData);
      setRouters(routersData);
      setTechnicians(techniciansData);
      setNodes(Array.isArray(nodesData) ? nodesData : []);
      setAccessPoints(Array.isArray(apsData) ? apsData : []);
    } catch (error) {
      console.error('Error fetching data:', error);
      setError('Error al cargar datos: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    await fetchInitialData();
    setRefreshing(false);
  };

  const handleSubmit = async (formData) => {
    try {
      const method = selectedInstallation ? 'PUT' : 'POST';
      const url = selectedInstallation
        ? `/api/installations/${selectedInstallation.id}`
        : '/api/installations';

      const token = localStorage.getItem('token');
      if (!token) {
        throw new Error('No se encontró token de autenticación');
      }

      // Construir el objeto de datos para la API
      const apiData = {
        firstName: formData.firstName,
        lastName: formData.lastName,
        dni: formData.dni,
        email: formData.email,
        phone: formData.phone,
        address: formData.address,
        department: formData.department,
        province: formData.province,
        district: formData.district,
        queueName: formData.queueName,
        ipAddress: formData.ipAddress,
        macAddress: formData.macAddress,
        routerId: formData.routerId,
        planId: formData.planId,
        zoneId: formData.zoneId,
        technicianId: formData.technicianId,
        installationDate: formData.installationDate,
        installationCost: formData.installationCost,
        comments: formData.comments,
        installationStatus: formData.installationStatus,
        coordinates: formData.coordinates,
        antenna_acquisition_type: formData.antenna_acquisition_type,
        antenna_price: formData.antenna_price,
        router_acquisition_type: formData.router_acquisition_type,
        router_price: formData.router_price
      };

      console.log('Enviando datos:', apiData);

      const response = await fetch(url, {
        method,
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(apiData)
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Error al guardar la instalación');
      }

      await fetchInitialData();
      setOpenForm(false);
      setSelectedInstallation(null);
    } catch (error) {
      console.error('Error saving installation:', error);
      throw error;
    }
  };

  const handleActivate = async (id) => {
    if (!window.confirm('¿Está seguro de que desea activar esta instalación? Se creará el cliente y se configurará la queue en MikroTik.')) {
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`/api/installations/${id}/activate`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Error al activar la instalación');
      }

      alert('Instalación activada exitosamente');
      fetchInitialData();
    } catch (error) {
      console.error('Error activating installation:', error);
      setError('Error al activar la instalación: ' + error.message);
    }
  };

  const handleCancel = async (id) => {
    if (!window.confirm('¿Está seguro de que desea cancelar esta instalación?')) {
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`/api/installations/${id}/cancel`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Error al cancelar la instalación');
      }

      alert('Instalación cancelada exitosamente');
      fetchInitialData();
    } catch (error) {
      console.error('Error cancelling installation:', error);
      setError('Error al cancelar la instalación: ' + error.message);
    }
  };

  const handleGenerateInvoice = async (id) => {
    setTargetInstallation(id);
    setCreditModalOpen(true);
    setCreditModalLoading(true);
    setPaymentType('cash');
    setInstallmentsCount(3);
    setInstallments([]);
    
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`/api/installations/${id}/billing-defaults`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (!response.ok) {
        throw new Error('Error al obtener sugerencias de facturación');
      }
      
      const data = await response.json();
      setInstallationTotal(data.total_amount);
      
      // Inicializar el arreglo de cuotas
      calculateInstallments(data.total_amount, 3);
    } catch (err) {
      console.error('Error loading installation billing defaults:', err);
      alert(err.message || 'Error al cargar sugerencias de facturación');
      setCreditModalOpen(false);
    } finally {
      setCreditModalLoading(false);
    }
  };

  const calculateInstallments = (total, count) => {
    const parsedCount = parseInt(count) || 1;
    const baseAmount = Math.floor((total / parsedCount) * 100) / 100;
    const remainder = Math.round((total - (baseAmount * parsedCount)) * 100) / 100;
    
    const list = [];
    const today = new Date();
    
    for (let i = 0; i < parsedCount; i++) {
      let amt = baseAmount;
      if (i === parsedCount - 1) {
        amt = Math.round((baseAmount + remainder) * 100) / 100;
      }
      
      const paymentDate = new Date(today);
      paymentDate.setMonth(today.getMonth() + i);
      paymentDate.setDate(paymentDate.getDate() + 5);
      
      const cutoffDate = new Date(paymentDate);
      cutoffDate.setDate(paymentDate.getDate() + 5);
      
      const formatToInputDate = (d) => {
        try {
          return d.toISOString().split('T')[0];
        } catch (e) {
          return '';
        }
      };
      
      list.push({
        amount: amt.toFixed(2),
        payment_due_date: formatToInputDate(paymentDate),
        due_date: formatToInputDate(cutoffDate)
      });
    }
    
    setInstallments(list);
  };

  const confirmGenerateInvoice = async () => {
    if (!targetInstallation) return;
    setActionInProgress(true);
    
    try {
      const token = localStorage.getItem('token');
      
      const body = {
        payment_type: paymentType,
        installments: paymentType === 'credit' ? installments : []
      };
      
      const response = await fetch(`/api/installations/${targetInstallation}/generate-invoice`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(body)
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Error al generar factura');
      }

      alert('Comprobante(s) de instalación generado exitosamente');
      setCreditModalOpen(false);
      fetchInitialData();
    } catch (error) {
      console.error('Error generating invoice:', error);
      alert('Error al generar factura: ' + error.message);
    } finally {
      setActionInProgress(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('¿Está seguro de que desea eliminar esta instalación? Esta acción no se puede deshacer.')) {
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`/api/installations/${id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Error al eliminar la instalación');
      }

      alert('Instalación eliminada exitosamente');
      fetchInitialData();
    } catch (error) {
      console.error('Error deleting installation:', error);
      setError('Error al eliminar la instalación: ' + error.message);
    }
  };

  const handleReactivate = async (id) => {
    if (!window.confirm('¿Está seguro de que desea reactivar esta instalación?')) {
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`/api/installations/${id}/reactivate`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Error al reactivar la instalación');
      }

      alert('Instalación reactivada exitosamente');
      fetchInitialData();
    } catch (error) {
      console.error('Error reactivating installation:', error);
      setError('Error al reactivar la instalación: ' + error.message);
    }
  };

  const handleEdit = async (id) => {
    try {
      const token = localStorage.getItem('token');
      // Obtener datos completos de la instalación
      const response = await fetch(`/api/installations/${id}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        throw new Error('Error al obtener datos de la instalación');
      }

      const installationData = await response.json();

      // Formatear datos para el formulario
      const formattedInstallation = {
        id: installationData.id,
        firstName: installationData.first_name || '',
        lastName: installationData.last_name || '',
        dni: installationData.dni || '',
        email: installationData.email || '',
        phone: installationData.phone || '',
        address: installationData.address || '',
        department: installationData.department || '',
        province: installationData.province || '',
        district: installationData.district || '',
        queueName: installationData.queue_name || '',
        ipAddress: installationData.ip_address || '',
        macAddress: installationData.mac_address || '',
        installationDate: installationData.installation_date ? new Date(installationData.installation_date) : null,
        installationCost: installationData.installation_cost || '',
        comments: installationData.comments || '',
        technicianId: installationData.technician_id || '',
        routerId: installationData.router_id || '',
        planId: installationData.plan_id || '',
        zoneId: installationData.zone_id || '',
        coordinates: processCoordinatesFromAPI(installationData.coordinates),
        installationStatus: installationData.status || 'pending'
      };

      setSelectedInstallation(formattedInstallation);
      setOpenForm(true);
    } catch (error) {
      console.error('Error loading installation data:', error);
      setError('Error al cargar datos de la instalación: ' + error.message);
    }
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  if (loading) {
    return (
      <Box className="flex justify-center items-center h-64">
        <CircularProgress />
        <Typography className="ml-4">Cargando instalaciones...</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: 1, sm: 2, md: 3 } }}>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Box sx={{
        display: 'flex',
        flexDirection: { xs: 'column', sm: 'row' },
        justifyContent: 'space-between',
        alignItems: { xs: 'stretch', sm: 'center' },
        gap: 2,
        mb: 2
      }}>
        <Typography variant="h4" sx={{ fontSize: { xs: '1.5rem', sm: '1.875rem' }, fontWeight: 800, color: '#0f172a', letterSpacing: '-0.5px' }}>Instalaciones</Typography>
        <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 1.5, alignItems: 'center', width: { xs: '100%', sm: 'auto' } }}>
          <Button
            variant="outlined"
            color="secondary"
            startIcon={refreshing ? <CircularProgress size={20} color="inherit" /> : <RefreshIcon />}
            onClick={handleRefresh}
            disabled={refreshing}
            sx={{
              borderRadius: '10px',
              textTransform: 'none',
              fontWeight: 700,
              px: 3,
              borderColor: 'rgba(100, 116, 139, 0.4)',
              color: '#64748b',
              '&:hover': {
                borderColor: '#64748b',
                backgroundColor: 'rgba(100, 116, 139, 0.05)'
              },
              width: { xs: '100%', sm: 'auto' }
            }}
          >
            {refreshing ? 'Actualizando...' : 'Actualizar'}
          </Button>
          <Button
            variant="contained"
            color="primary"
            startIcon={<AddIcon />}
            onClick={() => {
              setSelectedInstallation(null);
              setOpenForm(true);
            }}
            sx={{
              borderRadius: '10px',
              textTransform: 'none',
              fontWeight: 700,
              px: 3,
              backgroundColor: '#6366f1',
              boxShadow: '0 4px 12px rgba(99, 102, 241, 0.25)',
              '&:hover': { backgroundColor: '#4f46e5' },
              width: { xs: '100%', sm: 'auto' }
            }}
          >
            Nueva Instalación
          </Button>
        </Box>
      </Box>

      {/* Barra de Filtros */}
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, mb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: { xs: '100%', sm: 'auto' } }}>
          <TextField
            placeholder="Buscar instalación..."
            variant="outlined"
            size="small"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ color: '#64748b' }} />
                </InputAdornment>
              ),
            }}
            sx={{ flex: 1, minWidth: 200, maxWidth: { sm: 280 }, '& .MuiOutlinedInput-root': { borderRadius: '10px', backgroundColor: 'white' } }}
          />

          <Tooltip title="Configurar columnas visibles">
            <IconButton
              onClick={(e) => setColumnSettingsAnchor(e.currentTarget)}
              sx={{
                border: '1px solid rgba(0,0,0,0.08)',
                borderRadius: '10px',
                backgroundColor: columnSettingsAnchor ? 'rgba(99, 102, 241, 0.08)' : 'white',
                color: columnSettingsAnchor ? '#6366f1' : '#64748b',
                width: 40,
                height: 40,
                flexShrink: 0,
                '&:hover': { backgroundColor: 'rgba(99, 102, 241, 0.08)', color: '#6366f1' }
              }}
            >
              <ViewColumnIcon />
            </IconButton>
          </Tooltip>
        </Box>

        {/* Popover de columnas */}
        <Menu
          anchorEl={columnSettingsAnchor}
          open={Boolean(columnSettingsAnchor)}
          onClose={() => setColumnSettingsAnchor(null)}
          PaperProps={{
            elevation: 3,
            sx: {
              borderRadius: '12px',
              border: '1px solid rgba(0,0,0,0.06)',
              boxShadow: '0 8px 30px rgba(0,0,0,0.12)',
              minWidth: 220,
              maxHeight: 400,
              p: 0
            }
          }}
          transformOrigin={{ horizontal: 'right', vertical: 'top' }}
          anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
        >
          <Box sx={{ px: 2, py: 1.5, borderBottom: '1px solid rgba(0,0,0,0.06)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#1e293b', fontSize: '13px' }}>Columnas Visibles</Typography>
            <Button size="small" onClick={handleResetColumns} sx={{ textTransform: 'none', fontSize: '11px', color: '#6366f1', fontWeight: 600, minWidth: 'auto', px: 1 }}>Restablecer</Button>
          </Box>
          <Box sx={{ py: 0.5 }}>
            {ALL_COLUMNS.map(col => (
              <MenuItem
                key={col.id}
                onClick={() => handleColumnToggle(col.id)}
                disabled={col.alwaysVisible}
                dense
                sx={{ py: 0.5, px: 2, gap: 1 }}
              >
                <Checkbox
                  checked={isColumnVisible(col.id)}
                  disabled={col.alwaysVisible}
                  size="small"
                  sx={{ p: 0, mr: 1 }}
                />
                <Typography variant="body2" sx={{ fontSize: '13px', color: col.alwaysVisible ? '#94a3b8' : '#334155' }}>
                  {col.label}
                </Typography>
              </MenuItem>
            ))}
          </Box>
        </Menu>
      </Box>

      {/* Tabla Principal */}
      {!isMobile ? (
        <Paper
          elevation={1}
          sx={{
            borderRadius: '16px',
            overflow: 'hidden',
            border: '1px solid rgba(0, 0, 0, 0.05)',
            boxShadow: '0 4px 20px rgba(0,0,0,0.02)',
            backgroundColor: 'white'
          }}
        >
          <TableContainer sx={{
            maxHeight: 'calc(100vh - 280px)',
            overflowX: 'auto',
            '&::-webkit-scrollbar': {
              height: '6px',
              width: '6px'
            },
            '&::-webkit-scrollbar-thumb': {
              backgroundColor: 'rgba(99, 102, 241, 0.2)',
              borderRadius: '4px'
            }
          }}>
            <Table stickyHeader size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ backgroundColor: '#f8fafc', fontWeight: 700, color: '#475569', fontSize: '0.8rem', borderBottom: '1px solid rgba(0,0,0,0.06)' }}>ID</TableCell>
                  {isColumnVisible('client_name') && (
                    <TableCell sx={{ backgroundColor: '#f8fafc', fontWeight: 700, color: '#475569', fontSize: '0.8rem', borderBottom: '1px solid rgba(0,0,0,0.06)' }}>
                      <TableSortLabel
                        active={sortConfig.key === 'client_name'}
                        direction={sortConfig.key === 'client_name' ? sortConfig.direction : 'asc'}
                        onClick={() => requestSort('client_name')}
                        sx={{
                          '&.MuiTableSortLabel-active': { color: '#6366f1' },
                          '&.MuiTableSortLabel-active .MuiTableSortLabel-icon': { color: '#6366f1 !important' }
                        }}
                      >
                        Cliente
                      </TableSortLabel>
                    </TableCell>
                  )}
                  {isColumnVisible('queue_name') && (
                    <TableCell sx={{ backgroundColor: '#f8fafc', fontWeight: 700, color: '#475569', fontSize: '0.8rem', borderBottom: '1px solid rgba(0,0,0,0.06)' }}>
                      <TableSortLabel
                        active={sortConfig.key === 'queue_name'}
                        direction={sortConfig.key === 'queue_name' ? sortConfig.direction : 'asc'}
                        onClick={() => requestSort('queue_name')}
                        sx={{
                          '&.MuiTableSortLabel-active': { color: '#6366f1' },
                          '&.MuiTableSortLabel-active .MuiTableSortLabel-icon': { color: '#6366f1 !important' }
                        }}
                      >
                        Queue
                      </TableSortLabel>
                    </TableCell>
                  )}
                  {isColumnVisible('ip_address') && (
                    <TableCell sx={{ backgroundColor: '#f8fafc', fontWeight: 700, color: '#475569', fontSize: '0.8rem', borderBottom: '1px solid rgba(0,0,0,0.06)' }}>
                      <TableSortLabel
                        active={sortConfig.key === 'ip_address'}
                        direction={sortConfig.key === 'ip_address' ? sortConfig.direction : 'asc'}
                        onClick={() => requestSort('ip_address')}
                        sx={{
                          '&.MuiTableSortLabel-active': { color: '#6366f1' },
                          '&.MuiTableSortLabel-active .MuiTableSortLabel-icon': { color: '#6366f1 !important' }
                        }}
                      >
                        IP
                      </TableSortLabel>
                    </TableCell>
                  )}
                  {isColumnVisible('address') && (
                    <TableCell sx={{ backgroundColor: '#f8fafc', fontWeight: 700, color: '#475569', fontSize: '0.8rem', borderBottom: '1px solid rgba(0,0,0,0.06)' }}>
                      <TableSortLabel
                        active={sortConfig.key === 'address'}
                        direction={sortConfig.key === 'address' ? sortConfig.direction : 'asc'}
                        onClick={() => requestSort('address')}
                        sx={{
                          '&.MuiTableSortLabel-active': { color: '#6366f1' },
                          '&.MuiTableSortLabel-active .MuiTableSortLabel-icon': { color: '#6366f1 !important' }
                        }}
                      >
                        Dirección
                      </TableSortLabel>
                    </TableCell>
                  )}
                  {isColumnVisible('technician') && (
                    <TableCell sx={{ backgroundColor: '#f8fafc', fontWeight: 700, color: '#475569', fontSize: '0.8rem', borderBottom: '1px solid rgba(0,0,0,0.06)' }}>
                      <TableSortLabel
                        active={sortConfig.key === 'technician'}
                        direction={sortConfig.key === 'technician' ? sortConfig.direction : 'asc'}
                        onClick={() => requestSort('technician')}
                        sx={{
                          '&.MuiTableSortLabel-active': { color: '#6366f1' },
                          '&.MuiTableSortLabel-active .MuiTableSortLabel-icon': { color: '#6366f1 !important' }
                        }}
                      >
                        Técnico
                      </TableSortLabel>
                    </TableCell>
                  )}
                  {isColumnVisible('installation_date') && (
                    <TableCell sx={{ backgroundColor: '#f8fafc', fontWeight: 700, color: '#475569', fontSize: '0.8rem', borderBottom: '1px solid rgba(0,0,0,0.06)' }}>
                      <TableSortLabel
                        active={sortConfig.key === 'installation_date'}
                        direction={sortConfig.key === 'installation_date' ? sortConfig.direction : 'asc'}
                        onClick={() => requestSort('installation_date')}
                        sx={{
                          '&.MuiTableSortLabel-active': { color: '#6366f1' },
                          '&.MuiTableSortLabel-active .MuiTableSortLabel-icon': { color: '#6366f1 !important' }
                        }}
                      >
                        Fecha Instalación
                      </TableSortLabel>
                    </TableCell>
                  )}
                  {isColumnVisible('installation_cost') && (
                    <TableCell sx={{ backgroundColor: '#f8fafc', fontWeight: 700, color: '#475569', fontSize: '0.8rem', borderBottom: '1px solid rgba(0,0,0,0.06)' }}>
                      <TableSortLabel
                        active={sortConfig.key === 'installation_cost'}
                        direction={sortConfig.key === 'installation_cost' ? sortConfig.direction : 'asc'}
                        onClick={() => requestSort('installation_cost')}
                        sx={{
                          '&.MuiTableSortLabel-active': { color: '#6366f1' },
                          '&.MuiTableSortLabel-active .MuiTableSortLabel-icon': { color: '#6366f1 !important' }
                        }}
                      >
                        Costo
                      </TableSortLabel>
                    </TableCell>
                  )}
                  {isColumnVisible('status') && (
                    <TableCell sx={{ backgroundColor: '#f8fafc', fontWeight: 700, color: '#475569', fontSize: '0.8rem', borderBottom: '1px solid rgba(0,0,0,0.06)' }}>
                      <TableSortLabel
                        active={sortConfig.key === 'status'}
                        direction={sortConfig.key === 'status' ? sortConfig.direction : 'asc'}
                        onClick={() => requestSort('status')}
                        sx={{
                          '&.MuiTableSortLabel-active': { color: '#6366f1' },
                          '&.MuiTableSortLabel-active .MuiTableSortLabel-icon': { color: '#6366f1 !important' }
                        }}
                      >
                        Estado
                      </TableSortLabel>
                    </TableCell>
                  )}
                  <TableCell sx={{ backgroundColor: '#f8fafc', fontWeight: 700, color: '#475569', fontSize: '0.8rem', borderBottom: '1px solid rgba(0,0,0,0.06)', textAlign: 'center' }}>Acciones</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {sortedInstallations
                  .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                  .map((inst) => (
                    <TableRow
                      hover
                      key={inst.id}
                      sx={{
                        transition: 'all 0.2s ease',
                        '&:hover': { backgroundColor: '#f8fafc' }
                      }}
                    >
                      <TableCell sx={{ py: 0.8, fontFamily: 'monospace', color: '#64748b', fontSize: '0.8rem' }}>
                        <Tooltip title={inst.id}>
                          <span>{inst.id.substring(0, 8)}...</span>
                        </Tooltip>
                      </TableCell>
                      {isColumnVisible('client_name') && <TableCell sx={{ py: 0.8, fontWeight: 700, color: '#1e293b', fontSize: '0.825rem' }}>{inst.client_name}</TableCell>}
                      {isColumnVisible('queue_name') && <TableCell sx={{ py: 0.8, fontFamily: 'monospace', color: '#475569', fontSize: '0.8rem' }}>{inst.queue_name}</TableCell>}
                      {isColumnVisible('ip_address') && <TableCell sx={{ py: 0.8, fontFamily: 'monospace', fontWeight: 600, color: '#0f172a', fontSize: '0.8rem' }}>{inst.ip_address}</TableCell>}
                      {isColumnVisible('address') && <TableCell sx={{ py: 0.8, color: '#64748b', fontSize: '0.8rem', maxWidth: '200px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{inst.address}</TableCell>}
                      {isColumnVisible('technician') && <TableCell sx={{ py: 0.8, color: '#64748b', fontSize: '0.8rem', fontWeight: 500 }}>{inst.technician}</TableCell>}
                      {isColumnVisible('installation_date') && (
                        <TableCell sx={{ py: 0.8, color: '#64748b', fontSize: '0.8rem' }}>
                          {inst.installation_date ? new Date(inst.installation_date).toLocaleDateString('es-ES') : ''}
                        </TableCell>
                      )}
                      {isColumnVisible('installation_cost') && (
                        <TableCell sx={{ py: 0.8, fontWeight: 700, color: '#475569', fontSize: '0.8rem' }}>
                          {inst.installation_cost ? `S/. ${parseFloat(inst.installation_cost).toFixed(2)}` : ''}
                        </TableCell>
                      )}
                      {isColumnVisible('status') && (
                        <TableCell sx={{ py: 0.8 }}>
                          <Chip
                            label={
                              inst.status === 'pending' ? 'Pendiente' :
                              inst.status === 'completed' ? 'Completada' :
                              inst.status === 'cancelled' ? 'Cancelada' : inst.status
                            }
                            size="small"
                            sx={{
                              backgroundColor: inst.status === 'completed' ? 'rgba(16, 185, 129, 0.08)' :
                                             inst.status === 'pending' ? 'rgba(245, 158, 11, 0.08)' :
                                             'rgba(239, 68, 68, 0.08)',
                              color: inst.status === 'completed' ? '#059669' :
                                     inst.status === 'pending' ? '#d97706' : '#ef4444',
                              border: `1px solid ${inst.status === 'completed' ? 'rgba(16, 185, 129, 0.15)' :
                                                    inst.status === 'pending' ? 'rgba(245, 158, 11, 0.15)' :
                                                    'rgba(239, 68, 68, 0.15)'}`,
                              fontWeight: 700,
                              borderRadius: '8px',
                              fontSize: '0.725rem',
                              height: '22px'
                            }}
                          />
                        </TableCell>
                      )}
                      <TableCell sx={{ py: 0.8, textAlign: 'center' }}>
                        <Box sx={{ display: 'flex', justifyContent: 'center', gap: 0.5 }}>
                          <Tooltip title="Editar">
                            <IconButton
                              size="small"
                              onClick={() => handleEdit(inst.id)}
                              sx={{
                                color: '#6366f1',
                                backgroundColor: 'rgba(99, 102, 241, 0.06)',
                                padding: '4px',
                                '&:hover': { backgroundColor: 'rgba(99, 102, 241, 0.12)' }
                              }}
                            >
                              <EditIcon fontSize="small" style={{ fontSize: '0.9rem' }} />
                            </IconButton>
                          </Tooltip>

                          {inst.status === 'pending' && (
                            <>
                              <Tooltip title="Activar e Instalar">
                                <IconButton
                                  size="small"
                                  onClick={() => handleActivate(inst.id)}
                                  sx={{
                                    color: '#10b981',
                                    backgroundColor: 'rgba(16, 185, 129, 0.06)',
                                    padding: '4px',
                                    '&:hover': { backgroundColor: 'rgba(16, 185, 129, 0.12)' }
                                  }}
                                >
                                  <ActivateIcon fontSize="small" style={{ fontSize: '0.9rem' }} />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="Cancelar Instalación">
                                <IconButton
                                  size="small"
                                  onClick={() => handleCancel(inst.id)}
                                  sx={{
                                    color: '#ef4444',
                                    backgroundColor: 'rgba(239, 68, 68, 0.06)',
                                    padding: '4px',
                                    '&:hover': { backgroundColor: 'rgba(239, 68, 68, 0.12)' }
                                  }}
                                >
                                  <CancelIcon fontSize="small" style={{ fontSize: '0.9rem' }} />
                                </IconButton>
                              </Tooltip>
                            </>
                          )}

                          {inst.status === 'completed' && (
                            <Tooltip title="Generar Factura">
                              <IconButton
                                size="small"
                                onClick={() => handleGenerateInvoice(inst.id)}
                                sx={{
                                  color: '#3b82f6',
                                  backgroundColor: 'rgba(59, 130, 246, 0.06)',
                                  padding: '4px',
                                  '&:hover': { backgroundColor: 'rgba(59, 130, 246, 0.12)' }
                                }}
                              >
                                <InvoiceIcon fontSize="small" style={{ fontSize: '0.9rem' }} />
                              </IconButton>
                            </Tooltip>
                          )}

                          {inst.status === 'cancelled' && (
                            <>
                              <Tooltip title="Reactivar">
                                <IconButton
                                  size="small"
                                  onClick={() => handleReactivate(inst.id)}
                                  sx={{
                                    color: '#6366f1',
                                    backgroundColor: 'rgba(99, 102, 241, 0.06)',
                                    padding: '4px',
                                    '&:hover': { backgroundColor: 'rgba(99, 102, 241, 0.12)' }
                                  }}
                                >
                                  <RefreshIcon fontSize="small" style={{ fontSize: '0.9rem' }} />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="Eliminar Permanente">
                                <IconButton
                                  size="small"
                                  onClick={() => handleDelete(inst.id)}
                                  sx={{
                                    color: '#ef4444',
                                    backgroundColor: 'rgba(239, 68, 68, 0.06)',
                                    padding: '4px',
                                    '&:hover': { backgroundColor: 'rgba(239, 68, 68, 0.12)' }
                                  }}
                                >
                                  <DeleteIcon fontSize="small" style={{ fontSize: '0.9rem' }} />
                                </IconButton>
                              </Tooltip>
                            </>
                          )}
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))}
                {sortedInstallations.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={visibleColumns.length + 2} align="center" sx={{ py: 4, color: '#64748b' }}>
                      No se encontraron instalaciones
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            rowsPerPageOptions={[10, 25, 50]}
            component="div"
            count={sortedInstallations.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            labelRowsPerPage="Filas por página:"
            labelDisplayedRows={({ from, to, count }) => `${from}-${to} de ${count}`}
            sx={{ borderTop: '1px solid rgba(0,0,0,0.04)' }}
          />
        </Paper>
      ) : (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {sortedInstallations
            .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
            .map((inst) => (
              <Paper
                key={inst.id}
                sx={{
                  p: 1.5,
                  borderRadius: 2,
                  borderLeft: `3px solid ${
                    inst.status === 'completed' ? '#4caf50' :
                    inst.status === 'pending' ? '#ff9800' : '#f44336'
                  }`,
                  backgroundColor: 'white',
                  boxShadow: '0 1px 3px rgba(0,0,0,0.05)'
                }}
                elevation={1}
              >
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                  <Typography variant="body2" sx={{ fontWeight: 'bold', fontSize: '0.875rem', truncate: true }}>
                    {inst.client_name}
                  </Typography>
                  <Chip
                    label={
                      inst.status === 'pending' ? 'Pendiente' :
                      inst.status === 'completed' ? 'Completada' :
                      inst.status === 'cancelled' ? 'Cancelada' : inst.status
                    }
                    color={
                      inst.status === 'completed' ? 'success' :
                      inst.status === 'pending' ? 'warning' : 'error'
                    }
                    size="small"
                    sx={{ height: 18, fontSize: '0.65rem', px: 0.5 }}
                  />
                </Box>

                <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 0.5, mb: 0.75, fontSize: '0.725rem', lineHeight: 1.3, color: 'text.secondary' }}>
                  <Box sx={{ display: 'flex', gap: 0.5, minWidth: 0 }}>
                    <span style={{ color: '#888', fontWeight: 'bold' }}>IP:</span>
                    <span style={{ color: '#333', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{inst.ip_address || '-'}</span>
                  </Box>
                  <Box sx={{ display: 'flex', gap: 0.5, minWidth: 0 }}>
                    <span style={{ color: '#888', fontWeight: 'bold' }}>Queue:</span>
                    <span style={{ color: '#333', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{inst.queue_name || '-'}</span>
                  </Box>
                  <Box sx={{ display: 'flex', gap: 0.5, minWidth: 0 }}>
                    <span style={{ color: '#888', fontWeight: 'bold' }}>Técnico:</span>
                    <span style={{ color: '#333', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{inst.technician || '-'}</span>
                  </Box>
                  <Box sx={{ display: 'flex', gap: 0.5, minWidth: 0 }}>
                    <span style={{ color: '#888', fontWeight: 'bold' }}>Costo:</span>
                    <span style={{ color: '#333' }}>{inst.installation_cost ? `S/. ${parseFloat(inst.installation_cost).toFixed(2)}` : '-'}</span>
                  </Box>
                  <Box sx={{ display: 'flex', gap: 0.5, minWidth: 0, gridColumn: 'span 2' }}>
                    <span style={{ color: '#888', fontWeight: 'bold' }}>Fecha:</span>
                    <span style={{ color: '#333' }}>{inst.installation_date ? new Date(inst.installation_date).toLocaleDateString('es-ES') : '-'}</span>
                  </Box>
                </Box>

                {inst.address && (
                  <Box sx={{ fontSize: '0.725rem', mb: 1, color: 'text.secondary', display: 'flex', gap: 0.5, minWidth: 0 }}>
                    <span style={{ color: '#888', fontWeight: 'bold' }}>Dir:</span>
                    <span style={{ color: '#555', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{inst.address}</span>
                  </Box>
                )}

                <Box sx={{ display: 'flex', justifyContent: 'space-around', alignItems: 'center', pt: 0.5, borderTop: '1px solid #f0f0f0' }}>
                  <Tooltip title="Editar">
                    <IconButton
                      size="small"
                      onClick={() => handleEdit(inst.id)}
                      color="primary"
                      sx={{
                        p: 0.5,
                        backgroundColor: 'rgba(99, 102, 241, 0.06)',
                        '&:hover': { backgroundColor: 'rgba(99, 102, 241, 0.12)' }
                      }}
                    >
                      <EditIcon style={{ fontSize: 16 }} />
                    </IconButton>
                  </Tooltip>

                  {inst.status === 'pending' && (
                    <>
                      <Tooltip title="Activar e Instalar">
                        <IconButton
                          size="small"
                          onClick={() => handleActivate(inst.id)}
                          color="success"
                          sx={{
                            p: 0.5,
                            backgroundColor: 'rgba(16, 185, 129, 0.06)',
                            '&:hover': { backgroundColor: 'rgba(16, 185, 129, 0.12)' }
                          }}
                        >
                          <ActivateIcon style={{ fontSize: 16 }} />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Cancelar">
                        <IconButton
                          size="small"
                          onClick={() => handleCancel(inst.id)}
                          color="error"
                          sx={{
                            p: 0.5,
                            backgroundColor: 'rgba(239, 68, 68, 0.06)',
                            '&:hover': { backgroundColor: 'rgba(239, 68, 68, 0.12)' }
                          }}
                        >
                          <CancelIcon style={{ fontSize: 16 }} />
                        </IconButton>
                      </Tooltip>
                    </>
                  )}

                  {inst.status === 'completed' && (
                    <Tooltip title="Generar Factura">
                      <IconButton
                        size="small"
                        onClick={() => handleGenerateInvoice(inst.id)}
                        color="info"
                        sx={{
                          p: 0.5,
                          backgroundColor: 'rgba(59, 130, 246, 0.06)',
                          '&:hover': { backgroundColor: 'rgba(59, 130, 246, 0.12)' }
                        }}
                      >
                        <InvoiceIcon style={{ fontSize: 16 }} />
                      </IconButton>
                    </Tooltip>
                  )}

                  {inst.status === 'cancelled' && (
                    <>
                      <Tooltip title="Reactivar">
                        <IconButton
                          size="small"
                          onClick={() => handleReactivate(inst.id)}
                          color="primary"
                          sx={{
                            p: 0.5,
                            backgroundColor: 'rgba(99, 102, 241, 0.06)',
                            '&:hover': { backgroundColor: 'rgba(99, 102, 241, 0.12)' }
                          }}
                        >
                          <RefreshIcon style={{ fontSize: 16 }} />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Eliminar Permanente">
                        <IconButton
                          size="small"
                          onClick={() => handleDelete(inst.id)}
                          color="error"
                          sx={{
                            p: 0.5,
                            backgroundColor: 'rgba(239, 68, 68, 0.06)',
                            '&:hover': { backgroundColor: 'rgba(239, 68, 68, 0.12)' }
                          }}
                        >
                          <DeleteIcon style={{ fontSize: 16 }} />
                        </IconButton>
                      </Tooltip>
                    </>
                  )}
                </Box>
              </Paper>
            ))}

          {sortedInstallations.length === 0 && (
            <Paper sx={{ p: 4, textAlign: 'center', color: 'text.secondary' }}>
              No se encontraron instalaciones
            </Paper>
          )}

          <TablePagination
            rowsPerPageOptions={[10, 25, 50]}
            component="div"
            count={sortedInstallations.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            labelRowsPerPage="Filas por página:"
            labelDisplayedRows={({ from, to, count }) => `${from}-${to} de ${count}`}
            sx={{ borderTop: '1px solid rgba(0,0,0,0.04)' }}
          />
        </Box>
      )}

      <InstallationForm
        open={openForm}
        onClose={() => {
          setOpenForm(false);
          setSelectedInstallation(null);
        }}
        onSubmit={handleSubmit}
        zones={zones}
        plans={plans}
        routers={routers}
        technicians={technicians}
        nodes={nodes}
        accessPoints={accessPoints}
        installation={selectedInstallation}
      />

      {/* Modal de Configuración de Facturación (Contado / Crédito) */}
      <Dialog
        open={creditModalOpen}
        onClose={actionInProgress || creditModalLoading ? undefined : () => setCreditModalOpen(false)}
        PaperProps={{
          sx: { borderRadius: '16px', p: 1 }
        }}
      >
        <DialogTitle sx={{ fontWeight: 'bold', pb: 1 }}>
          Comprobante de Instalación
        </DialogTitle>
        <DialogContent>
          {creditModalLoading ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', my: 4, gap: 2, minWidth: 320 }}>
              <CircularProgress size={40} thickness={4} />
              <Typography variant="body2" color="text.secondary">
                Obteniendo datos de instalación...
              </Typography>
            </Box>
          ) : (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5, minWidth: { xs: 280, sm: 420 }, mt: 1 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', p: 2, bgcolor: '#f8fafc', borderRadius: '12px', border: '1px solid #e2e8f0' }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 'bold', color: '#1e293b' }}>
                  Total a Facturar:
                </Typography>
                <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#4f46e5' }}>
                  S/ {installationTotal.toFixed(2)}
                </Typography>
              </Box>

              <FormControl component="fieldset">
                <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 1, color: '#334155' }}>
                  Método de Pago / Financiamiento
                </Typography>
                <RadioGroup
                  row
                  value={paymentType}
                  onChange={(e) => {
                    setPaymentType(e.target.value);
                    if (e.target.value === 'credit') {
                      calculateInstallments(installationTotal, installmentsCount);
                    }
                  }}
                >
                  <FormControlLabel value="cash" control={<Radio />} label="Pago Único (Contado)" />
                  <FormControlLabel value="credit" control={<Radio />} label="Al Crédito (Cuotas)" />
                </RadioGroup>
              </FormControl>

              {paymentType === 'credit' && (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  <FormControl fullWidth>
                    <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 0.5, color: '#334155' }}>
                      Número de Cuotas (Meses)
                    </Typography>
                    <Select
                      value={installmentsCount}
                      onChange={(e) => {
                        const val = e.target.value;
                        setInstallmentsCount(val);
                        calculateInstallments(installationTotal, val);
                      }}
                      size="small"
                      sx={{ borderRadius: '8px' }}
                    >
                      {[2, 3, 4, 5, 6, 8, 10, 12].map(n => (
                        <MenuItem key={n} value={n}>{n} Cuotas</MenuItem>
                      ))}
                    </Select>
                  </FormControl>

                  <Typography variant="subtitle2" sx={{ fontWeight: 'bold', color: '#334155', mt: 1 }}>
                    Detalle y Fechas de Cuotas
                  </Typography>

                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, maxHeight: 240, overflowY: 'auto', pr: 1 }}>
                    {installments.map((inst, index) => (
                      <Box key={index} sx={{ display: 'flex', gap: 2, alignItems: 'center', borderBottom: '1px solid #f1f5f9', pb: 1.5 }}>
                        <Typography variant="body2" sx={{ minWidth: 70, fontWeight: 'medium', color: '#475569' }}>
                          Cuota {index + 1}:
                        </Typography>
                        <TextField
                          label="Monto"
                          size="small"
                          type="number"
                          value={inst.amount}
                          onChange={(e) => {
                            const newInst = [...installments];
                            newInst[index].amount = e.target.value;
                            setInstallments(newInst);
                          }}
                          sx={{ width: 100 }}
                          inputProps={{ step: '0.01' }}
                        />
                        <TextField
                          label="Vence el"
                          type="date"
                          size="small"
                          value={inst.payment_due_date}
                          onChange={(e) => {
                            const newInst = [...installments];
                            newInst[index].payment_due_date = e.target.value;
                            try {
                              const payD = new Date(e.target.value);
                              payD.setDate(payD.getDate() + 5);
                              newInst[index].due_date = payD.toISOString().split('T')[0];
                            } catch (err) {}
                            setInstallments(newInst);
                          }}
                          InputLabelProps={{ shrink: true }}
                          fullWidth
                        />
                      </Box>
                    ))}
                  </Box>

                  <Box sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    p: 1.5,
                    bgcolor: Math.abs(installments.reduce((sum, i) => sum + parseFloat(i.amount || 0), 0) - installationTotal) < 0.05 ? '#f0fdf4' : '#fef2f2',
                    borderRadius: '8px',
                    border: '1px solid',
                    borderColor: Math.abs(installments.reduce((sum, i) => sum + parseFloat(i.amount || 0), 0) - installationTotal) < 0.05 ? '#bbf7d0' : '#fecaca'
                  }}>
                    <Typography variant="caption" sx={{ fontWeight: 'medium' }}>
                      Suma de Cuotas: S/ {installments.reduce((sum, i) => sum + parseFloat(i.amount || 0), 0).toFixed(2)}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Esperado: S/ {installationTotal.toFixed(2)}
                    </Typography>
                  </Box>
                </Box>
              )}

              {paymentType === 'cash' && (
                <Box sx={{ bgcolor: '#eff6ff', p: 2, borderRadius: '12px', border: '1px solid #bfdbfe' }}>
                  <Typography variant="caption" color="primary.dark" sx={{ display: 'block', mb: 0.5, fontWeight: 'bold' }}>
                    ℹ️ Pago Único de Contado
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                    * Se generará una sola factura de S/ {installationTotal.toFixed(2)}.
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                    * Fecha de Vencimiento de Pago: En 5 días.
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                    * Fecha de Corte de Servicio: En 10 días.
                  </Typography>
                </Box>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button
            onClick={() => setCreditModalOpen(false)}
            variant="outlined"
            disabled={actionInProgress}
            sx={{ borderRadius: '8px', textTransform: 'none' }}
          >
            Cancelar
          </Button>
          <Button
            onClick={confirmGenerateInvoice}
            variant="contained"
            color="primary"
            disabled={
              actionInProgress || 
              creditModalLoading || 
              (paymentType === 'credit' && Math.abs(installments.reduce((sum, i) => sum + parseFloat(i.amount || 0), 0) - installationTotal) >= 0.05)
            }
            sx={{ borderRadius: '8px', textTransform: 'none', px: 3 }}
          >
            {actionInProgress ? <CircularProgress size={24} color="inherit" /> : 'Confirmar y Generar'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Installations;